import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  fetchConsultations, acceptConsultation, rejectConsultation, markNoShow, startConsultation,
} from "@lawyer/services/lawyerApi";

const TABS = [
  { key: "upcoming", label: "Upcoming" },
  { key: "live", label: "Live" },
  { key: "completed", label: "Completed" },
  { key: "cancelled", label: "Cancelled" },
  { key: "no_show", label: "No-show" },
];

const STATUS_COLOR = {
  pending: "bg-amber-100 text-amber-800",
  confirmed: "bg-blue-100 text-blue-800",
  accepted: "bg-blue-100 text-blue-800",
  live: "bg-emerald-100 text-emerald-800",
  waiting_for_lawyer: "bg-amber-100 text-amber-800",
  waiting_for_user: "bg-amber-100 text-amber-800",
  completed: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-rose-100 text-rose-800",
  rejected: "bg-rose-100 text-rose-800",
  no_show: "bg-rose-100 text-rose-800",
  refunded: "bg-rose-100 text-rose-800",
};

export default function LawyerConsultations() {
  const [params, setParams] = useSearchParams();
  const bucket = params.get("bucket") || "upcoming";
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchConsultations({ bucket, limit: 50 });
      setData(r);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load"); }
    setLoading(false);
  }, [bucket]);

  useEffect(() => { load(); }, [load]);

  const onAccept = async (cid) => {
    try { await acceptConsultation(cid); toast.success("Accepted"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const onStart = async (cid) => {
    try { await startConsultation(cid); toast.success("Consultation started"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const onReject = async (cid) => {
    const reason = window.prompt("Reason for rejection? (shown to client)");
    if (reason == null) return;
    try { await rejectConsultation(cid, reason); toast.success("Rejected"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const onNoShow = async (cid) => {
    if (!window.confirm("Mark as no-show? The client won't be auto-refunded — admin handles that.")) return;
    try { await markNoShow(cid); toast.success("Marked no-show"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <>
      <div className="mb-6" data-testid="lawyer-consultations-header">
        <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>Consultations</h1>
        <p className="mt-1 text-sm text-slate-500">Accept incoming bookings, jump into live rooms, mark no-shows.</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-4" data-testid="lawyer-consultations-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setParams({ bucket: t.key })}
            data-testid={`tab-${t.key}`}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border transition ${
              bucket === t.key ? "bg-[#0A2540] text-[#D4AF37] border-[#0A2540]" : "bg-white border-slate-200 text-slate-600 hover:border-[#0A2540]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="consultations-list">
        {loading ? (
          <div className="p-10 text-center text-sm text-slate-400">Loading…</div>
        ) : data.items.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400">No consultations in this bucket</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {data.items.map((c) => {
              const cls = STATUS_COLOR[c.status] || "bg-slate-100 text-slate-700";
              return (
                <li key={c.id} className="p-4 hover:bg-slate-50">
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex-1 min-w-[220px]">
                      <p className="font-bold text-[#0A2540]">{c.user_name || "Client"}</p>
                      <p className="text-xs text-slate-500 truncate">
                        {c.consultation_type} · ₹{(c.amount || 0).toLocaleString("en-IN")}
                        {c.slot_date ? ` · ${c.slot_date} ${c.slot_time || ""}` : ""}
                        {" · ID "}{(c.id || "").slice(0, 8)}
                      </p>
                    </div>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${cls}`}>
                      {c.status || "pending"}
                    </span>
                    <div className="flex flex-wrap gap-1">
                      {(c.status === "pending" || c.status === "confirmed") && (
                        <>
                          <button onClick={() => onAccept(c.id)} className="px-2 py-1 text-[11px] font-bold rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`accept-${c.id}`}>
                            Accept
                          </button>
                          <button onClick={() => onReject(c.id)} className="px-2 py-1 text-[11px] font-bold rounded-md bg-rose-50 text-rose-700 border border-rose-200" data-testid={`reject-${c.id}`}>
                            Reject
                          </button>
                        </>
                      )}
                      {(c.status === "accepted" || c.status === "confirmed" || c.status === "pending") && c.payment_status === "paid" && (
                        <button onClick={() => onStart(c.id)} className="px-2 py-1 text-[11px] font-bold rounded-md bg-[#D4AF37] text-[#0A2540] border border-[#B8941F]" data-testid={`start-${c.id}`}>
                          Start
                        </button>
                      )}
                      {(c.status === "accepted" || c.status === "confirmed" || c.status === "live") && (
                        <Link to={`/consultations/${c.id}`} className="px-2 py-1 text-[11px] font-bold rounded-md bg-[#0A2540] text-[#D4AF37]" data-testid={`join-${c.id}`}>
                          {c.status === "live" ? "Join live" : "Open room"}
                        </Link>
                      )}
                      {bucket === "upcoming" && (c.status === "accepted" || c.status === "confirmed") && (
                        <button onClick={() => onNoShow(c.id)} className="px-2 py-1 text-[11px] font-bold rounded-md border border-slate-200 hover:border-rose-300" data-testid={`noshow-${c.id}`}>
                          Mark no-show
                        </button>
                      )}
                    </div>
                  </div>
                  {c.query && <p className="mt-2 text-xs text-slate-500 line-clamp-2">"{c.query}"</p>}
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <p className="mt-2 text-[11px] text-slate-400">{data.total} total in this bucket</p>
    </>
  );
}
